import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Receipt, Plus, DollarSign, BarChart3, Pencil, Trash2, X } from 'lucide-react'
import { PageHeader, StatCard, EmptyState, Button } from '@/components/ui'
import { useCosts } from '@/hooks/useCosts'

const fmt = (n: number) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n)

const CATEGORIES = ['rent','salary','utilities','marketing','supplies','transport','maintenance','tax','interest','other'] as const
const BEHAVIORS  = ['fixed','variable','semi_variable','one_time'] as const
const FREQUENCIES = ['monthly','quarterly','annual','one_time'] as const

const CATEGORY_LABELS: Record<string, string> = {
  rent: 'Sewa', salary: 'Gaji', utilities: 'Utilitas', marketing: 'Marketing',
  supplies: 'Perlengkapan', transport: 'Transport', maintenance: 'Pemeliharaan',
  tax: 'Pajak', interest: 'Bunga', other: 'Lainnya'
}
const BEHAVIOR_LABELS: Record<string, string> = {
  fixed: 'Tetap', variable: 'Variabel', semi_variable: 'Semi-Variabel', one_time: 'Sekali'
}
const FREQ_LABELS: Record<string, string> = {
  monthly: 'Per Bulan', quarterly: 'Per Kuartal', annual: 'Per Tahun', one_time: 'Sekali'
}

const EMPTY_FORM = { name: '', category: 'other', amount: '', frequency: 'monthly', costBehavior: 'fixed', notes: '' }

export function CostsPage() {
  const { businessId } = useParams<{ businessId: string }>()
  const { costs, summary, isLoading, createCost, updateCost, deleteCost } = useCosts(businessId)
  const [showForm, setShowForm] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState<typeof EMPTY_FORM>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  const openCreate = () => { setEditId(null); setForm(EMPTY_FORM); setShowForm(true) }
  const openEdit = (c: typeof costs[0]) => {
    setEditId(c.id)
    setForm({ name: c.name, category: c.category, amount: String(c.amount), frequency: c.frequency, costBehavior: c.costBehavior, notes: c.notes ?? '' })
    setShowForm(true)
  }
  const closeForm = () => { setShowForm(false); setEditId(null) }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    const payload = {
      name: form.name,
      category: form.category as any,
      amount: parseInt(form.amount) || 0,
      frequency: form.frequency as any,
      costBehavior: form.costBehavior as any,
      notes: form.notes || null
    }
    if (editId) await updateCost(editId, payload)
    else await createCost(payload)
    setSaving(false)
    closeForm()
  }

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Hapus biaya "${name}"?`)) return
    await deleteCost(id)
  }

  return (
    <div className="page-container">
      <PageHeader
        title="Biaya"
        description="Catat semua biaya tetap, variabel, operasional, dan biaya lainnya."
        breadcrumbs={[
          { label: 'Bisnis Saya', href: '/businesses' },
          { label: 'Overview', href: `/businesses/${businessId}` },
          { label: 'Biaya' },
        ]}
        action={
          <Button id="costs-add" variant="primary" size="sm" leftIcon={<Plus size={14} />} onClick={openCreate}>
            Tambah Biaya
          </Button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Biaya Tetap" value={summary ? fmt(summary.totalFixed) : 'Rp 0'} icon={DollarSign} subtitle="Per bulan (estimasi)" />
        <StatCard title="Total Biaya Var." value={summary ? fmt(summary.totalVariable) : 'Rp 0'} icon={Receipt} subtitle="Per bulan" />
        <StatCard title="Total Bulanan" value={summary ? fmt(summary.totalMonthly) : 'Rp 0'} icon={BarChart3} subtitle="Semua kategori" />
        <StatCard title="Jumlah Biaya" value={String(costs.length)} icon={Receipt} subtitle="Item tercatat" />
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">{editId ? 'Edit Biaya' : 'Tambah Biaya'}</h2>
              <button type="button" onClick={closeForm} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Nama Biaya *</label>
                <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-500"
                  placeholder="Contoh: Sewa Tempat" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Kategori</label>
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-500">
                    {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Perilaku Biaya</label>
                  <select value={form.costBehavior} onChange={e => setForm(f => ({ ...f, costBehavior: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-500">
                    {BEHAVIORS.map(b => <option key={b} value={b}>{BEHAVIOR_LABELS[b]}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Nominal (IDR) *</label>
                  <input required type="number" min="1" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-500"
                    placeholder="3000000" />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Frekuensi</label>
                  <select value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-500">
                    {FREQUENCIES.map(fr => <option key={fr} value={fr}>{FREQ_LABELS[fr]}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm text-slate-400 block mb-1">Catatan (opsional)</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-500 resize-none" />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="ghost" size="sm" onClick={closeForm} className="flex-1">Batal</Button>
              <Button type="submit" variant="primary" size="sm" className="flex-1" disabled={saving}>
                {saving ? 'Menyimpan...' : (editId ? 'Simpan' : 'Tambah Biaya')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {isLoading ? (
        <div className="text-slate-500 text-center py-8">Memuat data biaya...</div>
      ) : costs.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="Belum ada biaya tercatat"
          description="Catat semua biaya bisnis seperti sewa, gaji, utilitas, dan biaya operasional lainnya."
          action={{ label: 'Tambah Biaya Pertama', onClick: openCreate }}
        />
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-800/50 text-slate-400 border-b border-slate-800">
              <tr>
                <th className="px-5 py-3 font-medium">Nama Biaya</th>
                <th className="px-5 py-3 font-medium">Kategori</th>
                <th className="px-5 py-3 font-medium">Perilaku</th>
                <th className="px-5 py-3 font-medium text-right">Nominal</th>
                <th className="px-5 py-3 font-medium">Frekuensi</th>
                <th className="px-5 py-3 font-medium text-center">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {costs.map(c => (
                <tr key={c.id} className="hover:bg-slate-800/20 transition-colors">
                  <td className="px-5 py-3">
                    <div className="font-medium text-slate-200">{c.name}</div>
                    {c.notes && <div className="text-xs text-slate-500 truncate max-w-[180px]">{c.notes}</div>}
                  </td>
                  <td className="px-5 py-3 text-slate-400">{CATEGORY_LABELS[c.category] ?? c.category}</td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                      c.costBehavior === 'fixed' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                      c.costBehavior === 'variable' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                      'bg-slate-700/50 text-slate-400 border border-slate-600'
                    }`}>
                      {BEHAVIOR_LABELS[c.costBehavior] ?? c.costBehavior}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right font-medium text-rose-400">{fmt(c.amount)}</td>
                  <td className="px-5 py-3 text-slate-400 text-sm">{FREQ_LABELS[c.frequency] ?? c.frequency}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => openEdit(c)} className="p-1 text-slate-400 hover:text-white transition-colors rounded"><Pencil size={14} /></button>
                      <button onClick={() => handleDelete(c.id, c.name)} className="p-1 text-slate-400 hover:text-rose-400 transition-colors rounded"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-slate-700 bg-slate-800/30">
              <tr>
                <td colSpan={3} className="px-5 py-3 text-slate-400 text-sm font-medium">Total Estimasi Bulanan</td>
                <td className="px-5 py-3 text-right font-bold text-white">{summary ? fmt(summary.totalMonthly) : 'Rp 0'}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}
